/**
 * The Game view's words, in English and Simplified Chinese.
 *
 * Same shape as web/src/i18n.ts - a typed en/zh/vi/hi quartet, a tr() helper, plain
 * `game.*` keys - and the keys fold in there verbatim when the site dictionary
 * takes them over. Until then the plate resolves its static chrome through the
 * fallback main.ts registers with i18n's applyStatic(), and it takes its
 * language from i18n's current value: one switch changes the whole page.
 *
 * Technical vocabulary stays Latin in both languages: JEV, choice/score/noul,
 * block, tick, state root, probability, the direction keys.
 */

import { currentLang, type Lang } from "../i18n";

const en = {
  "game.aria": "Game - the arena",
  "game.kicker": "THE ARENA",
  "game.title": "oBrain play game",
  "game.sub": "One Arc block · one thought · one hunt",
  "game.source": "oBrain × JEV",
  "game.foods": "Foods",
  "game.perFood": "Steps / food",
  "game.steps": "Steps",
  "game.length": "Length",
  "game.block": "Block",
  "game.cadence": "Chain",
  "game.pace": "Pace",
  "game.model": "Model",
  "game.interventions": "interventions",
  "game.session": "arena session · {steps} steps · {foods} foods · {pace} moves/s",
  "game.sessionLoading": "arena session · loading…",
  "game.sessionFailed": "arena session unavailable · {reason}",
  "game.controls":
    "SPACE pause · ↑ ↓ pace · R new round · click the arena to use them · {pace} moves/s now",
  "game.controlsPlay":
    "SPACE play · ↑ ↓ pace · R new round · click the arena to use them · {pace} moves/s now",
  "game.role.policy": "the policy engine's step",
  "game.role.chosen": "the model's own step",
  "game.role.follow": "following the plan it chose",
  "game.plan": "step {slot} of {size} · {role}",
  "game.decision": "Hunt step {move}",
  "game.dir.label": "Direction",
  "game.dir.type": "type: choice",
  "game.dir.up": "up",
  "game.dir.down": "down",
  "game.dir.left": "left",
  "game.dir.right": "right",
  "game.dir.chosen": "played",
  "game.dir.refused": "refused",
  "game.conf.label": "Confidence",
  "game.conf.type": "type: score",
  "game.conf.score": "score",
  "game.conf.sigma": "model σ",
  "game.risk.label": "Plan risk",
  "game.risk.type": "type: noul",
  "game.risk.noul": "noul",
  "game.stateIn": "State in",
  "game.stateIn.food": "food {vector}",
  "game.stateIn.danger": "danger · {list}",
  "game.stateIn.none": "danger · none",
  "game.stateIn.free": "free space {free} / {cells}",
  "game.stateIn.digest": "fired digest {digest} · bias {bias}",
  "game.stateIn.note":
    "the brain's thought at tick {tick}; the same digest seeds every decision below",
  "game.trail": "Decision trail",
  "game.trail.sub":
    "Newest first · one row per step · POLICY is the local engine's own answer, JEV is the model's, PLAN continues a plan a model chose",
  "game.col.block": "Block",
  "game.col.thought": "Tick · root",
  "game.col.move": "Move",
  "game.col.slot": "Step",
  "game.col.top": "Top p",
  "game.col.source": "Decided by",
  "game.col.latency": "Latency",
  "game.col.guard": "Guard",
  "game.tag.jev": "JEV",
  "game.tag.policy": "POLICY",
  "game.tag.plan": "PLAN",
  "game.guard.clean": "clean",
  "game.guard.cycle": "cycle guard → {direction}",
  "game.guard.fatal": "fatal move → {direction}",
  "game.guard.trapped": "trapped · no way out",
  "game.prov.block": "block {block} · {hash} · {txs} txs",
  "game.prov.rootPending": "pending",
  "game.prov.thought": "thought tick {tick} · root {root} · {fired} neurons fired",
  "game.prov.reusedThought":
    "thought tick {tick} · root {root} · {fired} fired · the brain had not thought again yet",
  "game.prov.state": "state the decision saw · {food} · {free} / {cells} free · length {length}",
  "game.prov.latency": "{ms} ms · {model}",
  "game.prov.policy": "no model call · the policy engine, decided when the run was composed",
  "game.row.tickReused": "reused {tick}",
  "game.row.rootPending": "root pending",
  "game.row.noThought": "no thought yet",
  "game.fig":
    "The arena is where the hunt happens: one apple, a body that grows with every meal, and walls that end it. The plate steps the arena here, in the browser, at the pace the controls set.",
} as const;

export type GameKey = keyof typeof en;

const zh: Record<GameKey, string> = {
  "game.aria": "游戏 - 竞技场",
  "game.kicker": "THE ARENA",
  "game.title": "oBrain 玩一局游戏",
  "game.sub": "一个 Arc 区块 · 一次思考 · 一场狩猎",
  "game.source": "oBrain × JEV",
  "game.foods": "吃到",
  "game.perFood": "步数 / 食物",
  "game.steps": "步数",
  "game.length": "长度",
  "game.block": "区块",
  "game.cadence": "链速",
  "game.pace": "速度",
  "game.model": "模型",
  "game.interventions": "次干预",
  "game.session": "竞技场对局 · {steps} 步 · {foods} 食物 · {pace} 步/秒",
  "game.sessionLoading": "竞技场对局 · 正在载入…",
  "game.sessionFailed": "竞技场对局不可用 · {reason}",
  "game.controls": "空格 暂停 · ↑ ↓ 调速 · R 重开 · 点击竞技场后按键生效 · 当前 {pace} 步/秒",
  "game.controlsPlay": "空格 播放 · ↑ ↓ 调速 · R 重开 · 点击竞技场后按键生效 · 当前 {pace} 步/秒",
  "game.role.policy": "策略引擎自己的选择",
  "game.role.chosen": "模型自己的一步",
  "game.role.follow": "继续它选定的计划",
  "game.plan": "第 {slot} / {size} 步 · {role}",
  "game.decision": "狩猎第 {move} 步",
  "game.dir.label": "方向",
  "game.dir.type": "类型：choice",
  "game.dir.up": "上",
  "game.dir.down": "下",
  "game.dir.left": "左",
  "game.dir.right": "右",
  "game.dir.chosen": "已执行",
  "game.dir.refused": "被拦下",
  "game.conf.label": "置信度",
  "game.conf.type": "类型：score",
  "game.conf.score": "分值",
  "game.conf.sigma": "模型 σ",
  "game.risk.label": "计划风险",
  "game.risk.type": "类型：noul",
  "game.risk.noul": "noul",
  "game.stateIn": "输入状态",
  "game.stateIn.food": "食物 {vector}",
  "game.stateIn.danger": "危险 · {list}",
  "game.stateIn.none": "危险 · 无",
  "game.stateIn.free": "空地 {free} / {cells}",
  "game.stateIn.digest": "放电摘要 {digest} · 偏向 {bias}",
  "game.stateIn.note": "大脑在第 {tick} 次思考的状态；下面的每个决策都由同一摘要播下",
  "game.trail": "决策轨迹",
  "game.trail.sub":
    "最新在前 · 每步一行 · POLICY 是本地引擎自己的答案，JEV 来自模型，PLAN 继续模型选定的计划",
  "game.col.block": "区块",
  "game.col.thought": "思考 · 状态根",
  "game.col.move": "移动",
  "game.col.slot": "步",
  "game.col.top": "最高 p",
  "game.col.source": "决策者",
  "game.col.latency": "延迟",
  "game.col.guard": "安全层",
  "game.tag.jev": "JEV",
  "game.tag.policy": "策略",
  "game.tag.plan": "计划",
  "game.guard.clean": "无干预",
  "game.guard.cycle": "闭环保护 → {direction}",
  "game.guard.fatal": "致命一步 → {direction}",
  "game.guard.trapped": "被困 · 无路可走",
  "game.prov.block": "区块 {block} · {hash} · {txs} 笔交易",
  "game.prov.rootPending": "待解码",
  "game.prov.thought": "思考 {tick} · 状态根 {root} · {fired} 个神经元放电",
  "game.prov.reusedThought": "思考 {tick} · 状态根 {root} · {fired} 个放电 · 大脑尚未再次思考",
  "game.prov.state": "决策看到的局面 · {food} · 空地 {free} / {cells} · 长度 {length}",
  "game.prov.latency": "{ms} 毫秒 · {model}",
  "game.prov.policy": "无模型调用 · 策略引擎，对局生成时已决定",
  "game.row.tickReused": "复用 {tick}",
  "game.row.rootPending": "状态根待解码",
  "game.row.noThought": "尚无思考",
  "game.fig":
    "竞技场就是狩猎发生的地方：一个苹果、一条每吃一口就变长的身体，以及会结束狩猎的墙。这一页在浏览器里按设定的速度推进它。",
};

const vi: Record<GameKey, string> = {
  "game.aria": "Trò chơi - đấu trường",
  "game.kicker": "ĐẤU TRƯỜNG",
  "game.title": "oBrain chơi",
  "game.sub": "Một khối Arc · một tư duy · một lần săn",
  "game.source": "oBrain × JEV",
  "game.foods": "Thức ăn",
  "game.perFood": "Bước / thức ăn",
  "game.steps": "Bước",
  "game.length": "Chiều dài",
  "game.block": "Khối",
  "game.cadence": "Chuỗi",
  "game.pace": "Nhịp",
  "game.model": "Mô hình",
  "game.interventions": "lần can thiệp",
  "game.session": "phiên đấu trường · {steps} bước · {foods} thức ăn · {pace} nước/giây",
  "game.sessionLoading": "phiên đấu trường · đang tải…",
  "game.sessionFailed": "phiên đấu trường không khả dụng · {reason}",
  "game.controls":
    "SPACE tạm dừng · ↑ ↓ nhịp · R vòng mới · bấm vào đấu trường để dùng · hiện {pace} nước/giây",
  "game.controlsPlay":
    "SPACE chơi · ↑ ↓ nhịp · R vòng mới · bấm vào đấu trường để dùng · hiện {pace} nước/giây",
  "game.role.policy": "bước của bộ máy chính sách",
  "game.role.chosen": "bước của chính mô hình",
  "game.role.follow": "đi theo kế hoạch nó đã chọn",
  "game.plan": "bước {slot} trên {size} · {role}",
  "game.decision": "Bước săn {move}",
  "game.dir.label": "Hướng",
  "game.dir.type": "loại: lựa chọn",
  "game.dir.up": "lên",
  "game.dir.down": "xuống",
  "game.dir.left": "trái",
  "game.dir.right": "phải",
  "game.dir.chosen": "đã chơi",
  "game.dir.refused": "từ chối",
  "game.conf.label": "Độ tin",
  "game.conf.type": "loại: điểm",
  "game.conf.score": "điểm",
  "game.conf.sigma": "σ mô hình",
  "game.risk.label": "Rủi ro kế hoạch",
  "game.risk.type": "loại: noul",
  "game.risk.noul": "noul",
  "game.stateIn": "Đầu vào trạng thái",
  "game.stateIn.food": "thức ăn {vector}",
  "game.stateIn.danger": "nguy hiểm · {list}",
  "game.stateIn.none": "nguy hiểm · không có",
  "game.stateIn.free": "chỗ trống {free} / {cells}",
  "game.stateIn.digest": "băm tập bắn {digest} · thiên lệch {bias}",
  "game.stateIn.note": "tư duy của não tại tick {tick}; cùng một băm gieo mọi quyết định bên dưới",
  "game.trail": "Vết quyết định",
  "game.trail.sub":
    "Mới nhất trước · mỗi bước một dòng · POLICY là câu trả lời của bộ máy cục bộ, JEV là của mô hình, PLAN tiếp một kế hoạch mô hình đã chọn",
  "game.col.block": "Khối",
  "game.col.thought": "Tick · gốc",
  "game.col.move": "Nước",
  "game.col.slot": "Bước",
  "game.col.top": "p đỉnh",
  "game.col.source": "Ai quyết định",
  "game.col.latency": "Độ trễ",
  "game.col.guard": "Canh",
  "game.tag.jev": "JEV",
  "game.tag.policy": "POLICY",
  "game.tag.plan": "PLAN",
  "game.guard.clean": "sạch",
  "game.guard.cycle": "canh vòng lặp → {direction}",
  "game.guard.fatal": "nước chết → {direction}",
  "game.guard.trapped": "bị nhốt · không lối ra",
  "game.prov.block": "khối {block} · {hash} · {txs} tx",
  "game.prov.rootPending": "đang chờ",
  "game.prov.thought": "tư duy tick {tick} · gốc {root} · {fired} neuron bắn",
  "game.prov.reusedThought": "tư duy tick {tick} · gốc {root} · {fired} bắn · não chưa tư duy lại",
  "game.prov.state":
    "trạng thái quyết định thấy · {food} · {free} / {cells} trống · chiều dài {length}",
  "game.prov.latency": "{ms} ms · {model}",
  "game.prov.policy": "không gọi mô hình · bộ máy chính sách, quyết định khi soạn phiên chạy",
  "game.row.tickReused": "dùng lại {tick}",
  "game.row.rootPending": "gốc đang chờ",
  "game.row.noThought": "chưa có tư duy",
  "game.fig":
    "Đấu trường là nơi cuộc săn diễn ra: một quả táo, một thân thể dài theo từng bữa ăn, và những bức tường kết thúc nó. Đĩa bước đấu trường tại đây, trong trình duyệt, với nhịp mà các phím điều khiển đặt.",
};

const hi: Record<GameKey, string> = {
  "game.aria": "खेल - अखाड़ा",
  "game.kicker": "अखाड़ा",
  "game.title": "oBrain खेल",
  "game.sub": "एक Arc ब्लॉक · एक विचार · एक शिकार",
  "game.source": "oBrain × JEV",
  "game.foods": "भोजन",
  "game.perFood": "कदम / भोजन",
  "game.steps": "कदम",
  "game.length": "लंबाई",
  "game.block": "ब्लॉक",
  "game.cadence": "शृंखला",
  "game.pace": "गति",
  "game.model": "मॉडल",
  "game.interventions": "हस्तक्षेप",
  "game.session": "अखाड़ा सत्र · {steps} कदम · {foods} भोजन · {pace} चाल/से",
  "game.sessionLoading": "अखाड़ा सत्र · लोड हो रहा है…",
  "game.sessionFailed": "अखाड़ा सत्र अनुपलब्ध · {reason}",
  "game.controls":
    "SPACE ठहराव · ↑ ↓ गति · R नया दौर · उन्हें चलाने के लिए अखाड़े पर क्लिक करें · अभी {pace} चाल/से",
  "game.controlsPlay":
    "SPACE चलाएँ · ↑ ↓ गति · R नया दौर · उन्हें चलाने के लिए अखाड़े पर क्लिक करें · अभी {pace} चाल/से",
  "game.role.policy": "नीति-इंजन का कदम",
  "game.role.chosen": "मॉडल का अपना कदम",
  "game.role.follow": "उसी योजना पर जो उसने चुनी",
  "game.plan": "{size} में कदम {slot} · {role}",
  "game.decision": "शिकार कदम {move}",
  "game.dir.label": "दिशा",
  "game.dir.type": "प्रकार: विकल्प",
  "game.dir.up": "ऊपर",
  "game.dir.down": "नीचे",
  "game.dir.left": "बायाँ",
  "game.dir.right": "दायाँ",
  "game.dir.chosen": "खेला गया",
  "game.dir.refused": "अस्वीकृत",
  "game.conf.label": "विश्वास",
  "game.conf.type": "प्रकार: स्कोर",
  "game.conf.score": "स्कोर",
  "game.conf.sigma": "मॉडल σ",
  "game.risk.label": "योजना जोखिम",
  "game.risk.type": "प्रकार: noul",
  "game.risk.noul": "noul",
  "game.stateIn": "अवस्था इनपुट",
  "game.stateIn.food": "भोजन {vector}",
  "game.stateIn.danger": "खतरा · {list}",
  "game.stateIn.none": "खतरा · कोई नहीं",
  "game.stateIn.free": "मुक्त स्थान {free} / {cells}",
  "game.stateIn.digest": "फायर डाइजेस्ट {digest} · झुकाव {bias}",
  "game.stateIn.note": "टिक {tick} पर मस्तिष्क का विचार; वही डाइजेस्ट नीचे के हर निर्णय को बीज देता है",
  "game.trail": "निर्णय-पथ",
  "game.trail.sub":
    "नवीनतम पहले · प्रति कदम एक पंक्ति · POLICY स्थानीय इंजन का उत्तर है, JEV मॉडल का, PLAN किसी मॉडल-चयनित योजना को जारी रखता है",
  "game.col.block": "ब्लॉक",
  "game.col.thought": "टिक · रूट",
  "game.col.move": "चाल",
  "game.col.slot": "कदम",
  "game.col.top": "शीर्ष p",
  "game.col.source": "निर्णायक",
  "game.col.latency": "विलंब",
  "game.col.guard": "रक्षा",
  "game.tag.jev": "JEV",
  "game.tag.policy": "POLICY",
  "game.tag.plan": "PLAN",
  "game.guard.clean": "स्वच्छ",
  "game.guard.cycle": "चक्र-रक्षा → {direction}",
  "game.guard.fatal": "घातक चाल → {direction}",
  "game.guard.trapped": "फँसा · कोई रास्ता नहीं",
  "game.prov.block": "ब्लॉक {block} · {hash} · {txs} tx",
  "game.prov.rootPending": "लंबित",
  "game.prov.thought": "विचार टिक {tick} · रूट {root} · {fired} न्यूरॉन फायर",
  "game.prov.reusedThought":
    "विचार टिक {tick} · रूट {root} · {fired} फायर · मस्तिष्क ने अभी फिर नहीं सोचा था",
  "game.prov.state": "वह अवस्था जो निर्णय ने देखी · {food} · {free} / {cells} मुक्त · लंबाई {length}",
  "game.prov.latency": "{ms} ms · {model}",
  "game.prov.policy": "कोई मॉडल कॉल नहीं · नीति-इंजन, रन रचे जाते समय निर्णीत",
  "game.row.tickReused": "पुनःप्रयुक्त {tick}",
  "game.row.rootPending": "रूट लंबित",
  "game.row.noThought": "अभी कोई विचार नहीं",
  "game.fig":
    "अखाड़ा वही है जहाँ शिकार होता है: एक सेब, हर भोजन के साथ बढ़ता शरीर, और उसे समाप्त करने वाली दीवारें। प्लेट अखाड़े को यहीं, ब्राउज़र में, उसी गति पर चलाती है जो नियंत्रण तय करते हैं।",
};

const DICT: Record<Lang, Record<GameKey, string>> = { en, zh, vi, hi };

/** Whether a data-i18n key is one of this plate's own. */
export function isGameKey(key: string): key is GameKey {
  return key in DICT.en;
}

type Params = Record<string, string>;

/** The copy for one key under an explicit language (pure: safe in tests). */
export function tr(lang: Lang, key: GameKey, params?: Params): string {
  let value: string = DICT[lang][key];

  if (params !== undefined) {
    for (const [name, replacement] of Object.entries(params))
      value = value.replaceAll(`{${name}}`, replacement);
  }

  return value;
}

/** The copy for one key under the current language. */
export function tg(key: GameKey, params?: Params): string {
  return tr(currentLang(), key, params);
}
